// Client IA du moteur — TRANSPORT INJECTÉ (prérequis Phase 1 du cadrage).
//
// Le cœur (construction de la requête chat/completions, plafonds de tokens,
// gestion de la famille gpt-5/o*, timeout, taxonomie d'erreurs) est identique
// pour toutes les apps. Ce qui varie — d'où vient la clé, quels en-têtes
// ajouter, vers quelle base URL partir — est fourni par CHAQUE app :
//  - extension : proxy hébergé + clé BYOK legacy dans chrome.storage + X-Anon-Id ;
//  - console   : clé serveur lue dans l'environnement, appel OpenAI direct.

import { OUTPUT_TOKEN_BUDGET, type ModelUsage } from './prompts'

/**
 * Nature d'un échec d'appel IA. Sert à l'UI pour choisir un message utile :
 *  - 'network' : la requête n'a JAMAIS abouti (fetch a rejeté, aucune réponse
 *    HTTP). Cas dominant en entreprise : un proxy/VPN filtre le domaine.
 *  - 'timeout' : l'API n'a pas répondu dans le délai imparti.
 *  - 'http'    : l'API a répondu, mais en erreur (quota, 4xx, 5xx…).
 */
/** `quota` : le proxy refuse parce que le quota d'adaptations de la semaine est
 *  atteint (429 `quota_atteint`, issue #120). Distinct de `http` pour que
 *  l'interface dise « limite atteinte » et jamais une panne. */
export type AiErrorKind = 'network' | 'timeout' | 'http' | 'quota'

export class AiCallError extends Error {
  readonly kind: AiErrorKind
  constructor(kind: AiErrorKind, message: string) {
    super(message)
    this.name = 'AiCallError'
    this.kind = kind
  }
}

/** `true` si l'API n'a pas pu être jointe du tout (filtrage réseau, hors ligne,
 *  DNS). À distinguer d'une erreur RENVOYÉE par l'API, qui, elle, prouve que la
 *  connexion fonctionne. */
export const isAiNetworkError = (err: unknown): boolean =>
  err instanceof AiCallError && err.kind === 'network'

export interface AiCallParams {
  /** Prompt utilisateur. */
  prompt: string
  /** Usage logique (sélectionne le bon modèle) — défaut: 'adapt'. */
  usage?: ModelUsage
  /** Surcharge explicite du modèle pour cet appel (tests / scripts uniquement). */
  model?: string
  /** Surcharge explicite de la clé pour cet appel (sinon : config.getApiKey). */
  apiKey?: string
  /** Température OpenAI. Défaut 0.7. */
  temperature?: number
  /** Forcer la réponse au format texte plutôt que JSON. */
  textResponse?: boolean
  /** Plafond de tokens générés. Omis => `OUTPUT_TOKEN_BUDGET[usage]`, jamais
   *  « pas de plafond » (cf. audit C2). Une valeur explicite plus basse gagne. */
  maxTokens?: number
  /** En-têtes propres à CET appel, ajoutés à `getExtraHeaders` : ceux du quota
   *  d'adaptations (`X-Usage`, `X-Offre`, `X-Radar-Jeton`, issue #120). Un
   *  objet simple, pour traverser le relais du service worker. */
  headers?: Record<string, string>
}

export interface AiClientConfig {
  /** Base de l'API compatible OpenAI (proxy hébergé ou api.openai.com). */
  baseUrl: string
  /**
   * Hôtes de secours, essayés dans l'ordre quand le principal est INJOIGNABLE.
   *
   * Sert à un cas précis et invisible autrement : le filtrage d'entreprise. Un
   * pare-feu qui sinkhole un domaine ne casse pas le service, il casse UN NOM —
   * un second hostname sur le même worker suffit à passer. Cf. le repli
   * `api.pebhub.be` de l'extension (`apps/extension/src/lib/ai.ts`).
   *
   * On ne bascule QUE sur 'network' et 'timeout' — jamais sur 'http'. Une
   * réponse HTTP en erreur prouve que l'hôte répond : la rejouer ailleurs
   * doublerait la charge d'un 429 et contournerait le coupe-circuit budgétaire,
   * qui est précisément là pour arrêter les frais.
   *
   * ⚠️ Le 'timeout' compte, et c'est contre-intuitif : un DNS sinkholé rend une
   * IP qui n'accepte pas la connexion, donc le fetch PEND jusqu'au timeout au
   * lieu de rejeter tout de suite. L'exclure laisserait le repli inutile face
   * au cas même pour lequel il existe.
   */
  fallbackBaseUrls?: string[]
  /** Modèle par usage, décidé au BUILD/déploiement (jamais par l'utilisateur). */
  models: Record<ModelUsage, string>
  /** Clé API à joindre en Authorization. Chaîne vide = pas d'en-tête (cas du
   *  proxy hébergé qui injecte la clé serveur). */
  getApiKey?: () => string | Promise<string>
  /** En-têtes additionnels par appel (X-Anon-Id, jeton client du proxy…). */
  getExtraHeaders?: () => Record<string, string> | Promise<Record<string, string>>
  /**
   * Notifié quand un hôte de SECOURS répond à la place du principal.
   *
   * C'est la seule mesure possible du filtrage d'entreprise : le repli fait son
   * travail en silence, donc « il n'a jamais servi » et « il sert tous les
   * jours » se ressemblent exactement — un support qui remonte à zéro ne dit pas
   * si le problème est réglé ou si le contournement le masque.
   *
   * Appelé une fois par BASCULE, pas par appel : les cinq ou six requêtes d'une
   * session d'adaptation partent toutes sur le secours mémorisé et n'ont rien de
   * plus à raconter. Un retour au principal réarme la notification.
   *
   * Injecté plutôt qu'implémenté ici : le paquet doit rester agnostique, c'est
   * l'app qui sait quoi faire de l'information (l'extension en fait un event
   * PostHog, la console n'a pas de repli).
   */
  onFallbackUsed?: (info: { host: string; primary: string; cause: AiErrorKind }) => void
  /** Timeout d'un appel. Défaut : 60 s. */
  timeoutMs?: number
  /** Injection de fetch (tests). Défaut : fetch global. */
  fetchImpl?: typeof fetch
  /**
   * Notifié à la fin de CHAQUE appel, réussi ou non, avec les jetons que l'API
   * annonce. C'est ce qui permet de chiffrer le coût réel (issue #95) : sans
   * lui, on ne pouvait que compter ce que les appels avaient produit. Comme
   * `onFallbackUsed`, une mesure qui échoue ne fait jamais échouer l'appel.
   */
  onAppel?: (appel: AppelIa) => void
}

/** Ce qu'un appel a coûté en jetons et en temps, tel que l'API le déclare. */
export interface AppelIa {
  usage: ModelUsage
  model: string
  ok: boolean
  /** Nature de l'échec, absente sur un succès. */
  erreur?: AiErrorKind
  promptTokens: number
  /** Part de `promptTokens` servie par le cache de prompt (facturée moins). */
  cachedTokens: number
  completionTokens: number
  ms: number
}

export type CallAI = (params: AiCallParams) => Promise<string>

export function createAiClient(config: AiClientConfig): { callAI: CallAI } {
  const timeoutMs = config.timeoutMs ?? 60000

  /**
   * Dernier hôte qui a RÉPONDU — essayé en premier au prochain appel.
   *
   * Sans cette mémoire, chaque appel repaie la découverte : sur un réseau qui
   * filtre le domaine principal, les cinq ou six appels d'une session
   * échoueraient d'abord sur lui avant de basculer. Anodin quand l'échec est
   * immédiat (DNS refusé), très visible quand l'hôte est sinkholé sur une IP
   * muette — le fetch pend alors jusqu'au timeout, soit une minute par appel.
   *
   * « Répondu » et non « réussi » : un 429 prouve que l'hôte est joignable,
   * c'est exactement ce qu'on cherche à mémoriser. Seul un échec de TOUS les
   * hôtes remet le compteur à zéro, pour repartir du principal — la préférence
   * ne doit pas se figer sur un secours devenu inutile.
   *
   * Vit le temps du client, donc d'un réveil du service worker côté extension.
   * Volontairement pas persisté : ça demanderait un stockage plateforme dans un
   * paquet qui doit rester agnostique, pour ne gagner que la première tentative
   * d'un réveil sur deux.
   */
  let hôtePréféré: string | undefined

  /** Dernier hôte de secours SIGNALÉ à l'app, pour ne notifier que les bascules
   *  et pas chaque appel de la session. Remis à zéro dès que le principal
   *  reprend la main : la bascule suivante est une information neuve. */
  let secoursSignalé: string | undefined

  const callAI: CallAI = async ({
    prompt,
    usage = 'adapt',
    model,
    apiKey,
    temperature = 0.7,
    textResponse = false,
    maxTokens,
    headers,
  }) => {
    const key = (apiKey ?? (await (config.getApiKey?.() ?? ''))).trim()
    const extraHeaders = { ...((await (config.getExtraHeaders?.() ?? {})) as Record<string, string>), ...(headers ?? {}) }

    const resolvedModel = model ?? config.models[usage]
    // Aucun appel ne part sans plafond de sortie (audit C2).
    const resolvedMaxTokens = maxTokens ?? OUTPUT_TOKEN_BUDGET[usage]
    // Famille gpt-5 et suivantes / o* : `temperature` est refusée (400) et le
    // raisonnement doit être coupé explicitement, sans quoi latence et coût
    // explosent. Le plafond de sortie s'appelle `max_completion_tokens` sur ces
    // modèles. gpt-6-luna refuse `max_tokens` exactement comme gpt-5.
    const isReasoningFamily = /^(gpt-[5-9]|o\d)/.test(resolvedModel)

    const doFetch = config.fetchImpl ?? fetch
    // Corps identique quel que soit l'hôte : construit une fois, rejoué tel quel
    // sur le secours.
    const corps = JSON.stringify({
      model: resolvedModel,
      messages: [{ role: 'user', content: prompt }],
      ...(isReasoningFamily ? { reasoning_effort: 'none' } : { temperature }),
      ...(isReasoningFamily
        ? { max_completion_tokens: resolvedMaxTokens }
        : { max_tokens: resolvedMaxTokens }),
      ...(textResponse ? {} : { response_format: { type: 'json_object' } }),
    })

    const déclarés = [config.baseUrl, ...(config.fallbackBaseUrls ?? [])]
    // L'hôte mémorisé passe devant, les autres gardent leur ordre de déclaration.
    // Le `includes` protège d'une préférence devenue obsolète (configuration
    // changée entre deux appels) : on ne veut pas appeler un hôte qui n'est plus
    // déclaré nulle part.
    const hôtes =
      hôtePréféré && déclarés.includes(hôtePréféré)
        ? [hôtePréféré, ...déclarés.filter((h) => h !== hôtePréféré)]
        : déclarés

    let dernièreErreur: AiCallError | undefined
    const début = Date.now()
    const signaler = (r: { ok: boolean; erreur?: AiErrorKind; data?: any }) => {
      try {
        const u = r.data?.usage ?? {}
        config.onAppel?.({
          usage,
          model: resolvedModel,
          ok: r.ok,
          ...(r.erreur ? { erreur: r.erreur } : {}),
          promptTokens: Number(u.prompt_tokens ?? 0),
          cachedTokens: Number(u.prompt_tokens_details?.cached_tokens ?? 0),
          completionTokens: Number(u.completion_tokens ?? 0),
          ms: Date.now() - début,
        })
      } catch {
        // Une mesure qui échoue ne doit jamais faire échouer l'appel mesuré.
      }
    }

    for (const hôte of hôtes) {
      // Un contrôleur NEUF par tentative : réutiliser le premier ferait partir
      // le secours avec un signal déjà avorté, donc un échec instantané que
      // rien ne distinguerait d'un vrai problème réseau.
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), timeoutMs)

      let response: Response
      try {
        response = await doFetch(`${hôte}/v1/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...extraHeaders,
            ...(key ? { 'Authorization': `Bearer ${key}` } : {}),
          },
          body: corps,
          signal: controller.signal,
        })
      } catch (err: any) {
        // `fetch` ne rejette QUE si la requête n'a pas pu aboutir : aucune
        // réponse HTTP n'est revenue (DNS, TLS, hors ligne, filtre web).
        dernièreErreur =
          err?.name === 'AbortError'
            ? new AiCallError('timeout', `Timeout : ${hôte} n'a pas répondu en ${Math.round(timeoutMs / 1000)} secondes.`)
            : new AiCallError(
                'network',
                `Service injoignable (${hôte}) — filtrage réseau ou connexion coupée. Cause d'origine : ${err?.message ?? err}`
              )
        continue
      } finally {
        clearTimeout(timeout)
      }

      // L'hôte a répondu : il est joignable, on le retiendra même si sa réponse
      // est une erreur.
      hôtePréféré = hôte

      if (hôte === config.baseUrl) {
        secoursSignalé = undefined
      } else if (hôte !== secoursSignalé) {
        secoursSignalé = hôte
        try {
          config.onFallbackUsed?.({
            host: hôte,
            primary: config.baseUrl,
            // `dernièreErreur` est forcément définie ici : on n'atteint un hôte
            // non principal qu'après l'échec d'au moins un précédent. La cause
            // distingue le refus DNS immédiat ('network') du sinkhole qui fait
            // pendre le fetch ('timeout') — deux filtrages très différents à
            // diagnostiquer, et impossibles à séparer après coup.
            cause: dernièreErreur?.kind ?? 'network',
          })
        } catch {
          // Une mesure qui échoue ne doit jamais faire échouer l'appel qu'elle
          // mesure : c'est l'inverse exact du service rendu.
        }
      }

      // Une erreur HTTP est définitive pour cet appel : on ne la rejoue pas
      // ailleurs (cf. `fallbackBaseUrls`).
      if (!response.ok) {
        const errorText = await response.text()
        // Le quota d'adaptations (#120) : un refus attendu, pas une panne. Le
        // message porte le JSON du proxy (utilisées, limite, remise), que
        // l'interface relit pour le dire.
        if (response.status === 429 && /"code"\s*:\s*"quota_atteint"/.test(errorText)) {
          signaler({ ok: false, erreur: 'quota' })
          throw new AiCallError('quota', errorText)
        }
        signaler({ ok: false, erreur: 'http' })
        throw new AiCallError('http', `AI API Error: ${errorText}`)
      }

      // `as any` : le type de retour de json() varie entre lib DOM et types Node.
      const data = (await response.json()) as any
      signaler({ ok: true, data })
      return data.choices[0].message.content
    }

    // Aucun hôte n'a répondu : la préférence n'a plus de sens, et la garder
    // ferait commencer le prochain appel par un hôte qu'on sait muet.
    hôtePréféré = undefined

    // Tous les hôtes injoignables. On remonte la dernière cause, en nommant
    // l'ensemble des tentatives — sinon le diagnostic accuserait un seul nom
    // alors que plusieurs ont échoué.
    signaler({ ok: false, erreur: dernièreErreur?.kind ?? 'network' })
    throw new AiCallError(
      dernièreErreur?.kind ?? 'network',
      hôtes.length > 1
        ? `Aucun hôte joignable (${hôtes.join(', ')}). Dernière cause : ${dernièreErreur?.message ?? 'inconnue'}`
        : (dernièreErreur?.message ?? `Service injoignable (${config.baseUrl}).`)
    )
  }

  return { callAI }
}
